import React, { useState } from "react";
import { toast } from "sonner";
import { useLanguage, useT } from "@/lib/LanguageContext";
import * as crmApi from "../api/crmApi";
import { crmErrorMessage } from "../api/crmErrors";
import { CONSENT_SOURCE_KEYS } from "../contract";
import { fmtWeekdayTime } from "../format";
import useRole from "../hooks/useRole";
import { datedFilename, downloadBlob } from "../../../lib/download";
import ConsentBadge from "../ui/ConsentBadge";
import CrmButton from "../ui/CrmButton";
import { CRM } from "../../../constants/testIds/crm";

/**
 * Consent evidence on the profile (spec Privacy): the badge, when ("Sat 21:40") and the
 * source ("Source: Capture form · meerayoga.com"), plus the owner's "Client data export" — the
 * client's right of access, one file.
 */
export default function ConsentCard({ client }) {
  const t = useT("crm");
  const tc = useT("common");
  const { lang } = useLanguage();
  const { can } = useRole();
  const [busy, setBusy] = useState(false);
  const c = client.consent || { status: client.consent_status };
  // The localized source label, then the host it was given on ("Capture form ·
  // meerayoga.com"). Never `source_detail`: that is the server's English audit line,
  // and the practitioner reads this card in their own language.
  const source = [CONSENT_SOURCE_KEYS[c.source] ? t(CONSENT_SOURCE_KEYS[c.source]) : "", c.source_host || ""]
    .filter(Boolean).join(" · ");

  const exportData = async () => {
    setBusy(true);
    try {
      downloadBlob(await crmApi.exportClientData(client.id), datedFilename(`client-${client.id}`, "json"));
    } catch (e) {
      toast.error(crmErrorMessage(e, t, tc, "errors.exportFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid={CRM.clientConsent} aria-labelledby="crm-consent-heading" className="rounded-2xl border border-eh-border-soft bg-eh-cream p-5">
      <h2 id="crm-consent-heading" className="font-display text-xl text-eh-ink">{t("consent.title")}</h2>
      <p className="mt-3 flex flex-wrap items-center gap-2 text-sm text-eh-ink">
        <ConsentBadge status={c.status} />
        {c.status === "unsubscribed" && c.unsubscribed_at ? fmtWeekdayTime(c.unsubscribed_at, lang) : c.at ? fmtWeekdayTime(c.at, lang) : ""}
      </p>
      {source && <p className="mt-2 text-sm text-eh-muted">{t("consent.source", { source })}</p>}
      {!c.at && c.status === "not_consented" && <p className="mt-2 text-sm text-eh-muted">{t("consent.none")}</p>}
      {can("mergeEraseDelete") && (
        <CrmButton variant="outline" className="mt-4" disabled={busy} onClick={exportData} data-testid={CRM.clientDataExport}>
          {t("consent.dataExport")}
        </CrmButton>
      )}
    </section>
  );
}
